const FALLBACK_CURRENCY = 'AED';

const safeArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const safeString = (value) => (typeof value === 'string' ? value.trim() : '');

const pickLang = (fr, en, ar, lang = 'En') => {
  if (lang === 'Ar') return safeString(ar) || safeString(en) || safeString(fr);
  if (lang === 'Fr') return safeString(fr) || safeString(en) || safeString(ar);
  return safeString(en) || safeString(fr) || safeString(ar);
};

const asNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const firstUrl = (items = []) =>
  safeArray(items)
    .filter((item) => !item?.kind || item.kind === 'image')
    .map((item) => (typeof item?.url === 'string' ? item.url.trim() : ''))
    .find(Boolean) || null;

export function getPropertyTitle(property, lang = 'En') {
  if (!property) return 'Property';
  return (
    pickLang(property.titleFr, property.titleEn, property.titleAr, lang) ||
    safeString(property.name) ||
    safeString(property.slug) ||
    'Property'
  );
}

export function getPropertyLocation(property) {
  if (!property) return '';
  const parts = [
    safeString(property?.location?.buildingName),
    safeString(property?.location?.community),
    safeString(property?.location?.district),
    safeString(property?.location?.city),
    safeString(property?.location?.country),
    safeString(property?.locationLabel),
  ].filter(Boolean);
  return parts.join(', ');
}

export function getPropertyCardImage(property) {
  if (!property) return null;
  const heroItems = safeArray(property?.hero?.media?.items);
  const heroFirst = heroItems.find((item) => item?.url);
  if (heroFirst?.url) return heroFirst.url;

  const galleryUrl = firstUrl(property?.gallery?.media?.items);
  if (galleryUrl) return galleryUrl;

  const visualUrl = firstUrl(property?.visual?.media?.items);
  if (visualUrl) return visualUrl;

  const developerLogo = safeString(property?.hero?.developerLogo) || safeString(property?.developerProfile?.logo);
  return developerLogo || null;
}

export function getPropertyPrimaryPrice(property) {
  if (!property) return null;

  const amount =
    asNumber(property?.basePrice) ??
    asNumber(property?.priceFrom) ??
    asNumber(property?.price) ??
    asNumber(property?.investment?.unitTypes?.[0]?.priceFrom) ??
    asNumber(property?.rental?.priceYearly) ??
    asNumber(property?.rental?.priceMonthly);

  if (amount === null) return null;

  return {
    amount,
    currency: safeString(property?.currency) || FALLBACK_CURRENCY,
    isFrom:
      asNumber(property?.priceFrom) !== null ||
      asNumber(property?.investment?.unitTypes?.[0]?.priceFrom) !== null,
  };
}

export function getPropertyPrice(property, currency = FALLBACK_CURRENCY) {
  const price = getPropertyPrimaryPrice(property);
  if (!price) return null;
  return {
    value: price.amount,
    currency: safeString(price.currency) || currency || FALLBACK_CURRENCY,
    isFrom: !!price.isFrom,
  };
}

export function getPropertyPriceSnapshot(property) {
  if (!property) return null;
  const snapshot = {
    usd: asNumber(property?.priceUsd ?? property?.priceUSD),
    eur: asNumber(property?.priceEur ?? property?.priceEUR),
    btc: asNumber(property?.priceBtc ?? property?.priceBTC),
    sar: asNumber(property?.priceSar ?? property?.priceSAR),
  };
  if (
    snapshot.usd === null &&
    snapshot.eur === null &&
    snapshot.btc === null &&
    snapshot.sar === null
  ) {
    return null;
  }
  return snapshot;
}

export function getPropertyArea(property) {
  if (!property) return null;
  return (
    asNumber(property?.interiorSurface) ??
    asNumber(property?.surface) ??
    asNumber(property?.builtUpArea)
  );
}

export function getPropertyRooms(property) {
  if (!property) return null;
  return asNumber(property?.rooms) ?? asNumber(property?.bedrooms) ?? asNumber(property?.beds);
}

export function getPropertyBathrooms(property) {
  if (!property) return null;
  return asNumber(property?.bathrooms) ?? asNumber(property?.baths);
}

export function getPropertySpecs(property) {
  return {
    beds: getPropertyRooms(property),
    baths: getPropertyBathrooms(property),
    surface: getPropertyArea(property),
    parking: asNumber(property?.parkingSpaces) ?? asNumber(property?.parking),
  };
}

export function getPropertyListingType(property) {
  const raw = safeString(property?.listingType).toLowerCase();
  const allowed = ['off-plan', 'secondary', 'rental', 'commercial', 'leisure'];
  return allowed.includes(raw) ? raw : 'secondary';
}

export function getPropertyStatus(property) {
  return safeString(property?.status) || safeString(property?.propertyStatus) || null;
}

export function getPropertyLandmarks(property) {
  if (!property) return [];
  if (Array.isArray(property?.location?.landmarks)) return property.location.landmarks.filter(Boolean);
  if (Array.isArray(property?.landmarks)) return property.landmarks.filter(Boolean);
  return [];
}

export function getPropertyListingBadge(property, lang = 'En') {
  const type = getPropertyListingType(property);
  const labels = {
    'off-plan': { Fr: 'Off-Plan', En: 'Off-Plan', Ar: 'Off-Plan' },
    secondary: { Fr: 'Secondaire', En: 'Secondary', Ar: 'Secondary' },
    rental: { Fr: 'Location', En: 'Rental', Ar: 'Rental' },
    commercial: { Fr: 'Commercial', En: 'Commercial', Ar: 'Commercial' },
    leisure: { Fr: 'Leisure', En: 'Leisure', Ar: 'Leisure' },
  };
  return {
    value: type,
    label: labels?.[type]?.[lang] || labels?.[type]?.En || 'Property',
  };
}

export function getPropertyCondensedFields(property) {
  if (!property) return {};
  const listingType = getPropertyListingType(property);

  switch (listingType) {
    case 'off-plan':
      return {
        developer: property?.hero?.developerName || property?.developerProfile?.titleEn || null,
        completionDate: property?.investment?.handoverDate ?? null,
        paymentPlan: property?.investment?.paymentPlan ?? null,
        estimatedROI: property?.investment?.estimatedROI ?? null,
        totalUnits: property?.investment?.totalUnits ?? null,
        unitTypes: property?.investment?.unitTypes ?? [],
      };
    case 'rental':
      return {
        furnishing: property?.furnishing ?? property?.rental?.furnished ?? null,
        availableFrom: property?.availableFrom ?? property?.rental?.availability ?? null,
      };
    case 'secondary':
      return {
        serviceCharge: property?.investment?.serviceCharge ?? null,
        view: property?.investment?.view ?? null,
        estimatedROI: property?.investment?.estimatedROI ?? null,
      };
    case 'commercial':
      return {
        usage: property?.usage ?? null,
        parking: property?.parkingSpaces ?? property?.parking ?? null,
      };
    case 'leisure':
      return {
        operator: property?.operator ?? null,
        usage: property?.usage ?? null,
      };
    default:
      return {};
  }
}
